import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { RESTServerRoute } from 'types/Server';
import { ENV, Env } from '@providers/env.provider';
import { API_VERSION_CTX, ApiVersion } from '../../types/api';
import { SKIP_REQUEST_MUTATORS } from '../skip-request-mutators/skip-request-mutators.interceptor';

@Injectable()
export class BaseUrlInterceptor implements HttpInterceptor {
  private static readonly urlsToExcludeWithStart = ['http'];
  private static readonly urlsToExcludeWithEnd = ['config.json'];

  private static readonly shouldUseAuthenticatedBaseApi = [
    'billing/scan-pay',
    RESTServerRoute.REST_SIGNIN,
    RESTServerRoute.REST_SIGNON,
    RESTServerRoute.REST_USER_SETTINGS_PUBLIC,
    RESTServerRoute.REST_PASSWORD_RESET,
    RESTServerRoute.REST_MAIL_CHECK,
    RESTServerRoute.REST_MAIL_RESEND,
    RESTServerRoute.REST_SSO_SIGNIN,
  ];

  /**
   * @deprecated put v2 in context like telematics.api.service.ts
   */
  private static readonly shouldUseV2Apis = [RESTServerRoute.REST_LOGS, RESTServerRoute.REST_LOG];

  private static readonly shouldUseUtilApis = [
    RESTServerRoute.REST_TENANT_LOGO,
    RESTServerRoute.REST_TENANT_SSO_OPTIONS,
  ];

  public constructor(@Inject(ENV) private env: Env) {}

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.context.get(SKIP_REQUEST_MUTATORS)) return next.handle(req);

    const isStaticAsset = (req.url.startsWith('assets/') || req.url.startsWith('/assets/')) && req.url.includes('.');
    const excludeStartWith = BaseUrlInterceptor.urlsToExcludeWithStart.some((exclude) => req.url.startsWith(exclude));
    const excludeEndWith = BaseUrlInterceptor.urlsToExcludeWithEnd.some((exclude) => req.url.endsWith(exclude));
    if (excludeStartWith || excludeEndWith || isStaticAsset) {
      return next.handle(req);
    }

    const requestUrl = req.url.startsWith('/') ? req.url.substring(1) : req.url;
    const clonedRequest = req.clone({
      url: this.getBaseUrl(req) + requestUrl,
    });

    return next.handle(clonedRequest);
  }

  private getBaseUrl(req: HttpRequest<any>): string {
    const url = req.url;
    if (BaseUrlInterceptor.shouldUseAuthenticatedBaseApi.some((useAuth) => url.includes(useAuth))) {
      return this.env().apiUrlEntity.authUrl;
    }

    if (req.context.get(API_VERSION_CTX) === ApiVersion.V2) {
      return this.env().apiUrlEntity.apiUrlV2;
    }
    if (BaseUrlInterceptor.shouldUseV2Apis.some((useV2) => url.startsWith(useV2))) {
      return this.env().apiUrlEntity.apiUrlV2;
    }

    if (BaseUrlInterceptor.shouldUseUtilApis.some((useUtil) => url.includes(useUtil))) {
      return this.env().apiUrlEntity.apiUtilUrl;
    }

    return this.env().apiUrlEntity.apiUrl;
  }
}
